import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ContextOperation } from "../../../../context/OperationContext";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useSnackbar from "../../../../hooks/useSnackbar";
import useToggle from "../../../../hooks/useToggle";
import { ContextDamageForm } from "../../DamageFormContext";
import GeneralInfoCard from "../../generalInfo/GeneralInfoCard";
import SectionCardActions from "../../SectionCardActions";
import SectionCardHeader from "../../SectionCardHeader";
import useDamageFormActions from "../../service/DamageFormActions";
import DamageFormService from "../../service/DamageFormService";
import { DamageFormTypes } from "../../Types";
import Dialogs from "../dialogs";
import TaskStatus, { ITaskStatus } from "../TaskStatus";
import EventDetails from "./EventDetails";
import VehicleStatus from "./VehicleStatus";
import { useHasScope } from "../../../../../../config/globalConfig";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";

const Task1 = () => {
  const hasScope = useHasScope;

  const { tenant, setLoading } = useContext(ContextOperation);
  const { generalInfo, task1, rsList } = useContext(ContextDamageForm);
  const {globalData} = useContext(GlobalContext);

  const t = useOperationTranslation();

  const snackBar = useSnackbar();

  const form = useForm<DamageFormTypes.ITask1Update>();

  const [openCloseConfirm, toggleCloseConfirm] = useToggle();

  const [taskStatus, setTaskStatus] = useState<ITaskStatus>({});
  const [loadComponent, setLoadComponent] = useState(0);

  const [reopenCloseConfirm, toggleReopenConfirm] = useToggle();

  const actions = useDamageFormActions();

  const { id } = useParams();

  useEffect(() => {
    if (!task1) {
      actions.getTask1();
    }

    setLoadComponent(1);
  }, []);

  const onFormReset = () => {
    form.reset({
      damageFormId: generalInfo?.id,
      dismantlingDate: task1?.dismantlingDate,
      legalLetter: task1?.legalLetter,
      plateLossComplaintDate: task1?.plateLossComplaintDate,
      repairDeliveryDate: task1?.repairDeliveryDate,
      repairRecoveryDate: task1?.repairRecoveryDate,
      repairShopAddress: task1?.repairShopAddress,
      repairShopCity: task1?.repairShopCity,
      repairShopMail: task1?.repairShopMail,
      repairShopName: task1?.repairShopName,
      repairShopNotes: task1?.repairShopNotes,
      repairShopPhone: task1?.repairShopPhone,
      repairShopRepresentative: task1?.repairShopRepresentative,
      rescuedBy: task1?.rescuedBy,
      sequestration: task1?.sequestration,
      sequestrationStartDate: task1?.sequestrationStartDate,
      sequestrationEndDate: task1?.sequestrationEndDate,
      seriousDamage: task1?.seriousDamage,
      formManagementLmsId: task1?.formManagementLms.id,
      repairShopSapId: rsList?.find(
        (shop) => shop.label === task1?.repairShopName
      )?.sapId,
      raMail: task1?.raMail,
      raId: task1?.raId,
      raOwner: task1?.raOwner,
      raNumber: task1?.raNumber,
      raSectional: task1?.raSectional,
      raDate: task1?.raDate,
      renewalsSubstitutions: task1?.renewalsSubstitutions,
      vehicle: task1?.vehicle,
      plate: task1?.plate,
      vin: task1?.vin,
      vehicleGroup: task1?.vehicleGroup,
      vehicleOwner: task1?.vehicleOwner,
      arrivalLmsId: task1?.arrivalLms.id,
      departureLmsId: task1?.departureLms.id,
      expectedRentalEnd: task1?.expectedRentalEnd,
      actualRentalEnd: task1?.actualRentalEnd, 
      rentalKm: task1?.rentalKm,
      inspectionKm: task1?.inspectionKm,
      rentalFuelLevel: task1?.rentalFuelLevel,
      inspectionFuelLevel: task1?.inspectionFuelLevel,
      rentalBatteryLevel: task1?.rentalBatteryLevel,
      inspectionBatteryLevel: task1?.inspectionBatteryLevel,      
      theftFormCode: task1?.theftFormCode,
      misappropriationFormCode: task1?.misappropriationFormCode,
      lmsTechnicalStop: task1?.lmsTechnicalStop,
      lmsTechnicalStopEnd: task1?.lmsTechnicalStopEnd,
      plateSubstitutionDate: task1?.plateSubstitutionDate,
      quoteReceivalDate: task1?.quoteReceivalDate,
      quoteNegationDate: task1?.quoteNegationDate,
      quoteAuthorizationDate: task1?.quoteAuthorizationDate,
      replacementOrderDate: task1?.replacementOrderDate,
      replacementUnavailabilityDate: task1?.replacementUnavailabilityDate,
      replacementArrivalDate: task1?.replacementArrivalDate,  
      repairStartDate: task1?.repairStartDate,
      repairEstimateDate: task1?.repairEstimateDate,
      repairEndDate: task1?.repairEndDate,
      deliveryDate: task1?.deliveryDate,
      customerSapId: task1?.customerSapId
      
    });
  };

  useEffect(() => {
    setTaskStatus({
      open: task1?.open,
      openingDate: task1?.createDate,
      openingUser: task1?.createUsername,
      closeDate: task1?.closeDate,
      closeUser: task1?.closeUsername,
    });

    onFormReset();
  }, [task1]);

  const getUpdate = (data: DamageFormTypes.ITask1Update) => {
    setLoading(true);
    DamageFormService.details.task1Update.put.updateTask1(data).then(
      () => {
        actions.getTask1();
		actions.getTask2();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task1.snackbar.update.success")
        );
        setLoading(false);
      },
      (error) => {

        if (error.response.status == 422) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error422")
          );
        }
        else if (error.response.status == 409) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error409")
          );
        }
        else if (error.response.status == 400) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error400")
          );
        }
        else if (error.response.status == 404) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error404")
          );
        }  else if (error.response.status == 406) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error406")
          );
        } else {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error")
          );
        }
        setLoading(false);
      }
    );
  };

  const onCloseTask1 = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task1.put.closeById(id).then(
      () => {
        toggleCloseConfirm();
        actions.getTask1();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task1.snackbar.close.success")
        );
        setLoading(false);
      },
      () => {
        toggleCloseConfirm();
        snackBar.show(
          "error",
          t("damageForm.details.task1.snackbar.close.error")
        );
        setLoading(false);
      }
    );
  };

  const onReopen = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task1.put.reopenByid(tenant, id).then(
      () => {
        toggleReopenConfirm();
        actions.getTask1();
		actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task1.snackbar.reopen.success")
        );
        setLoading(false);
      },
      () => {
        snackBar.show(
          "error",
          t("damageForm.details.task1.snackbar.reopen.error")
        );

        toggleReopenConfirm();
        setLoading(false);
      }
    );
  };

  const checkAuthDMSOperator = () => {

    if (task1?.formManagementLms != null && task1?.formManagementLms?.id != null) {

        return (hasScope("SCOPE_UPDATE_DAMAGE_TASK_ONE_MANAGEMENT_DMS") 
        && globalData?.user?.mobilityStores.filter(
            ({ sapId }: any) => sapId === task1?.formManagementLms?.id
          ).length > 0);

    } else {
        return hasScope("SCOPE_UPDATE_DAMAGE_TASK_ONE_MANAGEMENT_DMS");
    }
}

  return (
    <Mui.Box mt={2}>
      <form onSubmit={form.handleSubmit((data) => getUpdate(data))}>
        <Mui.Card>
          <SectionCardHeader>
            <Mui.Grid container spacing={2} mr={1}>
              {task1?.open &&
                !generalInfo?.migrated &&
                hasScope("SCOPE_CLOSE_DAMAGE_DAMAGE_FORM") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HighlightOffRounded />}
                      onClick={toggleCloseConfirm}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.task1.header.btnClose")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
              {!task1?.open &&
                !generalInfo?.migrated &&
                hasScope("SCOPE_REOPEN_DAMAGE_TASK_ONE") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HighlightOffRounded />}
                      onClick={toggleReopenConfirm}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.task1.header.btnReopen")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
            </Mui.Grid>
          </SectionCardHeader>

          <Mui.CardContent>
            <Mui.Stack spacing={2}>
              <GeneralInfoCard />

              {loadComponent >= 1 ? (
                <TaskStatus
                  formInfo={taskStatus}
                  onComponentDidMount={setLoadComponent}
                />
              ) : (
                <Mui.Skeleton variant="rectangular" width="100%">
                  <TaskStatus />
                </Mui.Skeleton>
              )}

              {loadComponent >= 2 ? (
                <VehicleStatus
                  task1Fields={task1}
                  onComponentDidMount={setLoadComponent}
                  formInstanceUpdate={form}
                />
              ) : (
                <Mui.Skeleton variant="rectangular">
                  <VehicleStatus />
                </Mui.Skeleton>
              )}

              {loadComponent >= 3 ? (
                <EventDetails task1Fields={task1} formInstanceUpdate={form} />
              ) : (
                <Mui.Skeleton variant="rectangular">
                  <EventDetails />
                </Mui.Skeleton>
              )}
            </Mui.Stack>
          </Mui.CardContent>

          {(hasScope("SCOPE_UPDATE_DAMAGE_TASK_ONE") || checkAuthDMSOperator()) && (
            <SectionCardActions onClickCancel={onFormReset} />
          )}
        </Mui.Card>
      </form>

      <Dialogs.CloseTaskConfirmation
        open={openCloseConfirm}
        onClose={toggleCloseConfirm}
        taskName={t("damageForm.details.tabs.task1")}
        onCloseTask={onCloseTask1}
      />

      <Dialogs.ReopenTaskConfirmation
        open={reopenCloseConfirm}
        onReopen={toggleReopenConfirm}
        onReopenTask={onReopen}
        taskName={t("damageForm.details.tabs.task1")}
      />
    </Mui.Box>
  );
};
export default Task1;
